import React from 'react';
import { Link2, Trash2 } from 'lucide-react';
import { ProjectNode } from '@/shared/types/projectData';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import { getMilestoneScheduleDate, getTaskScheduleEnd, getTaskScheduleStart } from '@/shared/utils/nodeSchedule';
import { isDocumentDisplayRecord } from '@/shared/utils/documentRecordClassification';
import { parseDateValuePreservingLocalDay } from '@/shared/utils/dates';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

/* ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================ */

export interface DependenciesSectionProps {
  dependencies?: string[];
  projectData: { nodes?: ProjectNode[] } | null;
  editedTaskId?: string;
  onDependenciesChange: (dependencies: string[] | undefined) => void;
  readOnly?: boolean;
  canLinkDependency?: (node: ProjectNode) => boolean;
  theme: ThemeClasses;
}

/* ============================================================================
 * DEPENDENCIES SECTION COMPONENT
 * Component for managing task dependencies
 * ============================================================================ */

export const DependenciesSection: React.FC<DependenciesSectionProps> = ({
  dependencies,
  projectData,
  editedTaskId,
  onDependenciesChange,
  readOnly = false,
  canLinkDependency,
  theme
}) => {
  const { locale, t } = useSettings();
  const nodes = projectData?.nodes || [];
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const editedTask = nodesById.get(editedTaskId || '');
  const canAddDependencies = !readOnly && !isDocumentDisplayRecord(editedTask || {}) &&
    (!editedTask || editedTask.type === 'task' || editedTask.type === 'milestone');
  const wouldCreateCycle = (nodeId: string): boolean => {
    const visited = new Set<string>();
    const pending = [nodeId];
    while (pending.length > 0) {
      const currentId = pending.pop()!;
      if (currentId === editedTaskId) return true;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      pending.push(...(nodesById.get(currentId)?.dependencies || []));
    }
    return false;
  };
  const canAddDependency = (node: ProjectNode): boolean =>
    canAddDependencies &&
    node.id !== editedTaskId &&
    !dependencies?.includes(node.id) &&
    (node.type === 'task' || node.type === 'milestone') &&
    !isDocumentDisplayRecord(node) &&
    (!canLinkDependency || canLinkDependency(node)) &&
    !wouldCreateCycle(node.id);

  const handleRemoveDependency = (depId: string) => {
    if (readOnly) return;
    const newDeps = dependencies?.filter(id => id !== depId) || [];
    onDependenciesChange(newDeps.length > 0 ? newDeps : undefined);
  };

  const handleAddDependency = (nodeId: string) => {
    const node = nodesById.get(nodeId);
    if (!node || !canAddDependency(node)) return;
    const currentDeps = dependencies || [];
    onDependenciesChange([...currentDeps, nodeId]);
  };

  const renderSchedule = (node: ProjectNode): string | null => {
    const formatDate = (value?: string): string | null => {
      if (!value) return null;
      const date = parseDateValuePreservingLocalDay(value);
      return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(locale);
    };
    if (node.type === 'milestone') {
      return formatDate(getMilestoneScheduleDate(node));
    }

    const startDate = getTaskScheduleStart(node);
    const endDate = getTaskScheduleEnd(node);
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    if (formattedStart && formattedEnd && startDate !== endDate) {
      return `${formattedStart} - ${formattedEnd}`;
    }
    return formattedEnd || formattedStart;
  };

  return (
    <div className="pm-task-dependencies space-y-4">
      <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
        <Link2 size={16} />
        {t('Dependencies')}
      </label>
      
      {/* Current Dependencies */}
      {dependencies && dependencies.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-slate-600 dark:text-gray-400">
            {t('Depends on:')}
          </div>
          <div className="space-y-1">
            {dependencies.map((depId) => {
              const depNode = nodesById.get(depId);
              const schedule = depNode ? renderSchedule(depNode) : null;
              
              return (
                <div
                  key={depId}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors group",
                    theme.bgSecondary,
                    theme.borderPrimary,
                    theme.surfaceHover
                  )}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{depNode ? depNode.title : t('Unavailable dependency')}</div>
                    {depNode && <div className="text-xs text-slate-600 dark:text-gray-400">
                      {depNode.type === 'milestone' ? t('Milestone') :
                       depNode.type === 'group' ? t('Group') : t('Task')}
                      {schedule && <span> • {schedule}</span>}
                    </div>}
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDependency(depId);
                      }}
                      className={cn(
                        "p-1 rounded hover:bg-red-500/20 text-red-500 transition-colors",
                        "opacity-0 group-hover:opacity-100 focus:opacity-100"
                      )}
                      title={t('Remove dependency')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Add Dependency */}
      {canAddDependencies && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-slate-600 dark:text-gray-400">
            {t('Add dependency:')}
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {nodes
              .filter(canAddDependency)
              .map((node: ProjectNode) => (
                <button
                  key={node.id}
                  type="button"
                  className={cn(
                    "group flex w-full items-center gap-2 p-2 rounded border text-left cursor-pointer transition-colors",
                    theme.bgSecondary,
                    theme.borderPrimary,
                    theme.surfaceHover,
                    theme.focusRing
                  )}
                  onClick={() => handleAddDependency(node.id)}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{node.title}</div>
                    <div className="text-xs text-slate-600 dark:text-gray-400">
                      {node.type === 'milestone' ? t('Milestone') : t('Task')}
                      {renderSchedule(node) && <span> • {renderSchedule(node)}</span>}
                    </div>
                  </div>
                  <div className="text-green-500 opacity-0 group-hover:opacity-100 group-focus:opacity-100">
                    <Link2 size={14} />
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
